import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import * as Icons from 'lucide-react';
import {
  Play, Pause, ChevronLeft, ChevronRight, X, RotateCcw, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TrainingPlayer
 * A modal that plays a single training module:
 *  - 'slideshow' : auto-advancing slides with title/caption/icon
 *  - 'video'     : embedded mp4/HLS video or YouTube/Vimeo iframe
 *  - 'tour'      : (deferred) currently shown as slideshow
 *
 * Reports progress + completion to the backend.
 */
export default function TrainingPlayer({
  module: trainingModule,
  open,
  onOpenChange,
  forced = false,            // forced = required gate (cannot close until complete)
  onCompleted,
}) {
  const [slideIdx, setSlideIdx] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const [completed, setCompleted] = useState(false);
  const timerRef = useRef(null);

  const slides = useMemo(
    () => (trainingModule?.slides && trainingModule.slides.length > 0 ? trainingModule.slides : []),
    [trainingModule]
  );
  const totalSlides = slides.length;
  const isVideo = trainingModule?.type === 'video' && trainingModule?.video_url;

  // Reset on open / module change
  useEffect(() => {
    if (!open || !trainingModule) return;
    setSlideIdx(0);
    setCompleted(false);
    setAutoplay(true);
    fetch(`${BACKEND_URL}/api/training/completions/${trainingModule.module_id}/start`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => { /* noop */ });
  }, [open, trainingModule]);

  // Auto-advance
  useEffect(() => {
    if (!open || isVideo || !autoplay || totalSlides === 0) return;
    const slide = slides[slideIdx] || {};
    const duration = slide.duration_ms || 4000;
    timerRef.current = setTimeout(() => {
      if (slideIdx < totalSlides - 1) {
        setSlideIdx((i) => i + 1);
      } else {
        finish();
      }
    }, duration);
    return () => clearTimeout(timerRef.current);
     
  }, [slideIdx, autoplay, open, totalSlides, isVideo]);

  // Save progress on slide change
  useEffect(() => {
    if (!open || !trainingModule || isVideo) return;
    fetch(`${BACKEND_URL}/api/training/completions/${trainingModule.module_id}/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ current_slide: slideIdx }),
    }).catch(() => { /* noop */ });
  }, [slideIdx, open, trainingModule, isVideo]);

  const finish = async () => {
    if (!trainingModule || completed) return;
    setCompleted(true);
    try {
      await fetch(`${BACKEND_URL}/api/training/completions/${trainingModule.module_id}/complete`, {
        method: 'POST',
        credentials: 'include',
      });
      toast.success(`Completed: ${trainingModule.title}`);
      onCompleted?.(trainingModule.module_id);
    } catch (_e) {
      toast.error('Failed to mark complete');
    }
  };

  const handleClose = (v) => {
    if (forced && !completed) {
      toast.warning('Please complete this required training to continue.');
      return;
    }
    onOpenChange?.(v);
  };

  if (!trainingModule) return null;

  const slide = slides[slideIdx] || {};
  const Icon = (slide.icon && Icons[toPascal(slide.icon)]) || Icons.Play;
  const accent = slide.accent || '#C9A962';
  const progress = totalSlides ? ((slideIdx + 1) / totalSlides) * 100 : (completed ? 100 : 0);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-2xl bg-[#2D2D2D] text-white border-[#C9A962] overflow-hidden p-0"
        data-testid="training-player"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/10">
          <DialogTitle className="text-[#C9A962] flex items-center gap-2">
            <Icons.GraduationCap className="h-5 w-5" /> {trainingModule.title}
            {trainingModule.required && (
              <span className="text-[10px] uppercase tracking-widest text-[#2D2D2D] bg-[#C9A962] px-2 py-0.5 rounded-full ml-2">
                Required
              </span>
            )}
          </DialogTitle>
          {trainingModule.description && (
            <p className="text-sm text-zinc-400">{trainingModule.description}</p>
          )}
        </DialogHeader>

        <div className="px-6 pt-4 pb-6">
          {isVideo ? (
            <VideoFrame url={trainingModule.video_url} />
          ) : completed ? (
            <CompletionScreen onClose={() => handleClose(false)} />
          ) : (
            <SlideStage slide={slide} Icon={Icon} accent={accent} index={slideIdx} total={totalSlides} />
          )}
        </div>

        {!isVideo && !completed && (
          <div className="px-6 pb-5">
            <Progress
              value={progress}
              className="h-1.5 bg-white/10"
              data-testid="training-progress"
            />
            <div className="mt-3 flex items-center justify-between gap-3">
              <Button
                variant="ghost"
                className="text-zinc-300 hover:text-white"
                disabled={slideIdx === 0}
                onClick={() => setSlideIdx((i) => Math.max(0, i - 1))}
                data-testid="training-prev"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button
                variant="ghost"
                className="text-[#C9A962] hover:text-white"
                onClick={() => setAutoplay((v) => !v)}
                data-testid="training-playpause"
              >
                {autoplay ? <><Pause className="h-4 w-4 mr-1" /> Pause</> : <><Play className="h-4 w-4 mr-1" /> Play</>}
              </Button>
              <Button
                className="font-bold"
                style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                onClick={() => slideIdx < totalSlides - 1 ? setSlideIdx((i) => i + 1) : finish()}
                data-testid="training-next"
              >
                {slideIdx < totalSlides - 1 ? <>Next <ChevronRight className="h-4 w-4 ml-1" /></> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Finish</>}
              </Button>
            </div>
            <p className="text-center text-[11px] text-zinc-500 mt-2">
              Slide {slideIdx + 1} of {totalSlides}
            </p>
          </div>
        )}

        {(isVideo || completed) && (
          <div className="px-6 pb-5 flex justify-between gap-3">
            {isVideo && !completed ? (
              <Button
                className="font-bold"
                style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                onClick={finish}
                data-testid="training-finish-video"
              >
                <CheckCircle2 className="h-4 w-4 mr-1" /> Mark as completed
              </Button>
            ) : <span />}
            {!forced && (
              <Button
                variant="outline"
                className="border-zinc-600 text-zinc-300"
                onClick={() => { setCompleted(false); setSlideIdx(0); }}
                data-testid="training-replay"
              >
                <RotateCcw className="h-4 w-4 mr-1" /> Replay
              </Button>
            )}
          </div>
        )}

        {!forced && (
          <button
            type="button"
            className="absolute right-3 top-3 text-zinc-500 hover:text-white"
            onClick={() => handleClose(false)}
            aria-label="Close"
            data-testid="training-close"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SlideStage({ slide, Icon, accent, index, total }) {
  return (
    <div
      key={index}
      className="rounded-xl p-8 flex flex-col items-center text-center min-h-[260px] justify-center transition-opacity duration-500"
      style={{
        background: `radial-gradient(circle at 50% 0%, ${accent}33 0%, transparent 60%), #1a1a1a`,
        border: `1px solid ${accent}55`,
      }}
      data-testid={`training-slide-${index}`}
    >
      <div
        className="w-20 h-20 rounded-2xl flex items-center justify-center mb-5 shadow-lg"
        style={{ backgroundColor: accent, color: '#1a1a1a' }}
      >
        <Icon className="w-10 h-10" strokeWidth={2.25} />
      </div>
      <h3
        className="text-2xl font-black tracking-tight mb-2"
        style={{ color: accent, fontFamily: 'Barlow Condensed, sans-serif' }}
      >
        {slide.title}
      </h3>
      <p className="text-sm sm:text-base text-zinc-200 max-w-md leading-relaxed">
        {slide.caption}
      </p>
      <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mt-5">
        Step {index + 1} / {total}
      </p>
    </div>
  );
}

function CompletionScreen({ onClose }) {
  return (
    <div
      className="rounded-xl p-8 flex flex-col items-center text-center min-h-[260px] justify-center"
      style={{
        background: 'radial-gradient(circle at 50% 0%, rgba(34,197,94,0.25) 0%, transparent 60%), #1a1a1a',
        border: '1px solid rgba(34,197,94,0.5)',
      }}
      data-testid="training-completed"
    >
      <CheckCircle2 className="w-16 h-16 text-green-400 mb-4" strokeWidth={2} />
      <h3 className="text-2xl font-black text-green-400 mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        Training Complete
      </h3>
      <p className="text-sm text-zinc-300 max-w-md">
        Nice work — you can replay anytime from your dashboard.
      </p>
      <Button
        className="mt-6 font-bold"
        style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
        onClick={onClose}
        data-testid="training-completed-close"
      >
        Close
      </Button>
    </div>
  );
}

function VideoFrame({ url }) {
  // Detect YouTube/Vimeo for iframe embedding
  const yt = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  const vimeo = url?.match(/vimeo\.com\/(\d+)/);
  if (yt) {
    return (
      <div className="aspect-video rounded-xl overflow-hidden border border-[#C9A962]/40">
        <iframe
          title="training-video"
          src={`https://www.youtube.com/embed/${yt[1]}`}
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
          data-testid="training-video-iframe"
        />
      </div>
    );
  }
  if (vimeo) {
    return (
      <div className="aspect-video rounded-xl overflow-hidden border border-[#C9A962]/40">
        <iframe
          title="training-video"
          src={`https://player.vimeo.com/video/${vimeo[1]}`}
          allow="autoplay; fullscreen"
          allowFullScreen
          className="w-full h-full"
          data-testid="training-video-iframe"
        />
      </div>
    );
  }
  return (
    <video
      src={url}
      controls
      className="w-full rounded-xl border border-[#C9A962]/40 bg-black"
      data-testid="training-video-player"
    />
  );
}

function toPascal(name) {
  return String(name)
    .split(/[-_ ]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
}
